// Récupération — recovery-days screen (soft-island port of the
// design/Récupération.html prototype, cg-* files). Splits récup out
// of the old combined Balances page into its own route (/recup),
// teal accent variant of the same island as Congés.
//
// Deviations from the prototype (deliberate):
// - shell chrome dropped (app shell + EntityBar own it),
// - credits are manual (Bouchra) or come from Espace employé
//   worked-day claims, surfaced here as decision cards; récup POSE
//   requests from the espace land on /balances "Demandes",
// - the team calendar is fed by /monthly-grid (per-entity).
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useUiStore } from '../store/ui.js';
import { toast } from '../store/toast.js';
import {
  useApproveClaim, useLeaveBalances, useLeaveTransactions, useRecupClaims, useRejectClaim,
  type AdminClaimDTO, type LeaveBalanceRow,
} from '../api/hooks.js';
import {
  CgCount, EmpCell, balanceByEmp, cgAvColor, cgFmtDate, cgFmtLong, cgInitials, cgNum, cgToday, cgD,
  entityById, matchesQuery, shiftYm, ymLabel,
} from '../components/conges/common.js';
import { LeaveCalendar } from '../components/conges/LeaveCalendar.js';
import { EmployeeDrawer } from '../components/conges/EmployeeDrawer.js';
import { SaisieModal, type SaisiePreset } from '../components/conges/SaisieModal.js';
import { CreditRecupModal } from '../components/conges/CreditRecupModal.js';
import '../styles/conges.css';

type View = 'stock' | 'calendrier';

export function RecuperationPage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entityId = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = searchParams.get('view') === 'calendrier' ? 'calendrier' : 'stock';
  const month = /^\d{4}-\d{2}$/.test(searchParams.get('month') ?? '') ? searchParams.get('month')! : cgToday().slice(0, 7);

  const [q, setQ] = useState('');
  const [drawerEmp, setDrawerEmp] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<SaisiePreset | null>(null);
  const [credit, setCredit] = useState<{ empId?: string | null } | null>(null);

  const recups = useLeaveBalances({ type: 'RECUP', entityId });
  const txs = useLeaveTransactions({ type: 'RECUP' });
  // Worked-day declarations from the Espace employé, awaiting a
  // manager's decision (approve = MANUAL_ADDITION credit).
  const claims = useRecupClaims({ entityId });

  const setParams = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setSearchParams(p, { replace: true });
  };
  const go = (v: View) => setParams({ view: v === 'stock' ? null : v });

  const rows = useMemo(
    () => (recups.data ?? []).filter((r) => matchesQuery(r.employee, q)),
    [recups.data, q],
  );
  const balMapFull = useMemo(() => balanceByEmp(recups.data), [recups.data]);
  const calBalMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of recups.data ?? []) map.set(r.employee.id, r.balance.current);
    return map;
  }, [recups.data]);

  const K = rows.reduce(
    (a, r) => ({
      stock: a.stock + r.balance.current,
      cred: a.cred + r.balance.manualAdded + r.balance.initialStock,
      pris: a.pris + r.balance.consumed,
      withStock: a.withStock + (r.balance.current > 0 ? 1 : 0),
    }),
    { stock: 0, cred: 0, pris: 0, withStock: 0 },
  );

  // Journal side panel: credits granted, most recent first, scoped to
  // the visible team.
  const journal = useMemo(() => {
    const visible = new Set(rows.map((r) => r.employee.id));
    return (txs.data ?? []).filter((t) => visible.has(t.employeeId)).slice(0, 30);
  }, [txs.data, rows]);

  const KPIS: Array<{ v: number; dec?: number; s?: string; cls?: string; l: string }> = [
    { v: K.stock, cls: 'info', s: 'j', l: `Stock de récup · ${rows.length} employés` },
    { v: K.cred, s: 'j', l: 'Journées week-end / férié créditées' },
    { v: K.pris, s: 'j', l: 'Journées de récup prises' },
    { v: K.withStock, dec: 0, l: 'Employés avec du stock' },
  ];

  return (
    <div className={'cgapp accent-recup' + (themeDark ? ' theme-dark' : '')}>
      <div className="cg-scrollpage">
        <div className="gm-wrap">
          <div className="gm-eyebrow">Données · Récupération</div>
          <h1 className="gm-title">Récupération <em>week-ends &amp; fériés</em></h1>
          <p className="gm-sub">
            Journées travaillées les week-ends et jours fériés, stock par employé, pose des journées
            de récup — chaque mouvement est tracé dans le journal.
          </p>

          <div className="cg-ctrl">
            <div className="cg-seg">
              {([['stock', 'Stock récup'], ['calendrier', 'Calendrier']] as const).map(([k, l]) => (
                <button key={k} className={view === k ? 'on' : ''} onClick={() => go(k)}>{l}</button>
              ))}
            </div>
            {view === 'calendrier' && (
              <div className="cg-monthnav">
                <button className="gm-mn-arrow" onClick={() => setParams({ month: shiftYm(month, -1) })}>‹</button>
                <span className="cg-mn-lbl">{ymLabel(month)}</span>
                <button className="gm-mn-arrow" onClick={() => setParams({ month: shiftYm(month, 1) })}>›</button>
                <button className="cg-mn-today" onClick={() => setParams({ month: null })}>Aujourd'hui</button>
              </div>
            )}
            <div className="cg-spacer" />
            <div className="cg-searchtop">
              <span>⌕</span>
              <input placeholder="Rechercher un employé…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <button className="cg-ghost" onClick={() => setSaisie({})}>↧ Poser une récup</button>
            <button className="gm-attn-go" onClick={() => setCredit({})}>+ Créditer une journée</button>
          </div>

          <div className="cg-rule">
            <span className="cg-rule-ic">i</span>
            <span>
              <b>1 journée travaillée un vendredi, un samedi ou un jour férié = 1 jour de récupération</b>{' '}
              · crédit manuel uniquement (pas d'acquisition automatique) · stock sans expiration,
              indépendant du solde de congés.
            </span>
          </div>

          <div className="cg-kpis four">
            {KPIS.map((k, i) => (
              <div key={i} className="cg-kpi">
                <span className={'cg-kpi-v ' + (k.cls ?? '')}>
                  <CgCount value={k.v} dec={k.dec === 0 ? 0 : 1} suffix={k.s} />
                </span>
                <span className="cg-kpi-l">{k.l}</span>
              </div>
            ))}
          </div>

          {view === 'stock' && (claims.data?.length ?? 0) > 0 && (
            <ClaimsPanel claims={claims.data!} onOpen={setDrawerEmp} />
          )}

          {view === 'stock' && (
            recups.isLoading
              ? <div className="cg-placeholder view-enter">Chargement du stock…</div>
              : <div className="cg-recupview view-enter">
                  <StockTable rows={rows} onOpen={setDrawerEmp}
                    onCredit={(empId) => setCredit({ empId })}
                    onNew={(empId) => setSaisie({ empId })} />
                  <div className="cg-rv-side">
                    <div className="cg-secttl">
                      Journal des mouvements
                      <span className="gm-th-count">{journal.length}</span>
                    </div>
                    <div className="cg-histlist">
                      {journal.map((t) => {
                        const emp = balMapFull.get(t.employeeId)?.employee;
                        const dow = cgD(t.date).getUTCDay();
                        return (
                          <div key={t.id} className="cg-hist" onClick={() => setDrawerEmp(t.employeeId)}>
                            <span className={'cg-hist-st gm-mono ' + (t.amount >= 0 ? 'recup' : 'refuse')}>
                              {t.amount >= 0 ? '+' : ''}{cgNum(t.amount)}
                            </span>
                            <div className="cg-hist-m">
                              <b>{emp?.name ?? '—'}</b>
                              <i>{t.reason ?? t.source} · {cgFmtDate(t.date)}</i>
                            </div>
                            <span className="cg-hist-by">{dow === 5 ? 'Ven.' : dow === 6 ? 'Sam.' : ''}</span>
                          </div>
                        );
                      })}
                      {journal.length === 0 && <div className="cg-empty sm">Aucun mouvement.</div>}
                    </div>
                  </div>
                </div>
          )}
          {view === 'calendrier' && (
            <LeaveCalendar entityId={entityId} month={month} types={['recup']} q={q}
              balMap={calBalMap} onOpen={setDrawerEmp} onNew={(empId, date) => setSaisie({ empId, date })} />
          )}
        </div>
      </div>

      {drawerEmp && (
        <EmployeeDrawer employeeId={drawerEmp} page="recup" onClose={() => setDrawerEmp(null)}
          onNew={(empId) => { setDrawerEmp(null); setSaisie({ empId }); }}
          onCredit={(empId) => { setDrawerEmp(null); setCredit({ empId }); }} />
      )}
      {saisie && <SaisieModal type="RECUP" entityId={entityId} preset={saisie} onClose={() => setSaisie(null)} />}
      {credit && <CreditRecupModal preset={credit} onClose={() => setCredit(null)} />}
    </div>
  );
}

// ─── Journées déclarées (Espace employé) ─────────────────────
// Decision cards mirroring the DemandesView pattern: approve credits
// the récup stock server-side, reject archives the claim.
function ClaimsPanel({ claims, onOpen }: {
  claims: AdminClaimDTO[];
  onOpen: (empId: string) => void;
}) {
  const approve = useApproveClaim();
  const reject = useRejectClaim();
  const [exiting, setExiting] = useState<Record<string, 'valide' | 'refuse'>>({});

  const decide = (claim: AdminClaimDTO, dec: 'valide' | 'refuse') => {
    setExiting((x) => ({ ...x, [claim.id]: dec }));
    window.setTimeout(async () => {
      try {
        if (dec === 'valide') {
          await approve.mutateAsync(claim.id);
          toast.success('Journée créditée au stock de récup');
        } else {
          await reject.mutateAsync({ id: claim.id });
          toast.success('Déclaration refusée');
        }
      } catch {
        setExiting((x) => { const { [claim.id]: _drop, ...rest } = x; return rest; });
        toast.danger('Échec — réessayez.');
      }
    }, 340);
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div className="cg-secttl">
        Journées travaillées déclarées
        <span className="gm-th-count">{claims.length}</span>
      </div>
      <div className="cg-cards">
        {claims.map((c, i) => {
          const ent = entityById(c.employee.entityId);
          const dow = cgD(c.date).getUTCDay();
          return (
            <div key={c.id} className={'cg-card' + (exiting[c.id] ? ' out ' + exiting[c.id] : '')} style={{ animationDelay: `${i * 40}ms` }}>
              <div className="cg-card-h">
                <span className="gm-av" style={{ background: cgAvColor(c.employee.id), cursor: 'pointer' }} onClick={() => onOpen(c.employee.id)}>
                  {cgInitials(c.employee.name)}
                </span>
                <div className="cg-card-id">
                  <b>{c.employee.name}</b>
                  <i>
                    {ent && <span className="cg-entdot" style={{ background: ent.color }} />}
                    {ent?.short ?? c.employee.entityId} · déclarée par l'employé
                  </i>
                </div>
                <span className="cg-typechip" style={{ background: 'var(--infos)', color: 'var(--infoc)' }}>+{cgNum(c.amount)} j récup</span>
              </div>
              <div className="cg-card-dates">
                <span className="cg-card-range">{cgFmtLong(c.date)}</span>
                <span className="cg-card-days gm-mono">{dow === 5 ? 'Vendredi' : dow === 6 ? 'Samedi' : 'Jour férié'}</span>
              </div>
              {c.motif && <div className="cg-card-note">« {c.motif} »</div>}
              <div className="cg-card-act" style={{ marginTop: 13 }}>
                <button className="cg-refuse" disabled={!!exiting[c.id]} onClick={() => decide(c, 'refuse')}>Refuser</button>
                <button className="cg-approve" disabled={!!exiting[c.id]} onClick={() => decide(c, 'valide')}><span>✓</span> Créditer</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Stock récup table ───────────────────────────────────────
function StockTable({ rows, onOpen, onCredit, onNew }: {
  rows: LeaveBalanceRow[];
  onOpen: (empId: string) => void;
  onCredit: (empId: string) => void;
  onNew: (empId: string) => void;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.balance.current - a.balance.current),
    [rows],
  );
  const T = sorted.reduce(
    (a, r) => ({
      report: a.report + r.balance.initialStock,
      cred: a.cred + r.balance.manualAdded,
      pris: a.pris + r.balance.consumed,
      stock: a.stock + r.balance.current,
    }),
    { report: 0, cred: 0, pris: 0, stock: 0 },
  );
  return (
    <div className="gm-gridwrap">
      <div className="cg-rv-head">
        <div>
          <div className="cg-secttl plain">Stock de récupération</div>
          <div className="cg-rv-sub">1 journée travaillée un vendredi, samedi ou jour férié = 1 jour de récup · sans expiration.</div>
        </div>
      </div>
      <div className="gm-scroll">
        <table className="sb-tbl">
          <thead>
            <tr>
              <th className="sb-cEmp">Employé</th>
              <th className="num">Report</th>
              <th className="num">+ Jours crédités</th>
              <th className="num">− Pris</th>
              <th className="num">= Stock récup</th>
              <th>Répartition</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const b = r.balance;
              return (
                <tr key={r.employee.id} className="cg-row" style={{ animationDelay: `${i * 18}ms` }} onClick={() => onOpen(r.employee.id)}>
                  <td className="sb-cEmp"><EmpCell emp={r.employee} /></td>
                  <td className="num gm-mono dim">{cgNum(b.initialStock)}</td>
                  <td className="num gm-mono up">+{cgNum(b.manualAdded)}</td>
                  <td className="num gm-mono down">{b.consumed ? `−${cgNum(b.consumed)}` : <span className="dim">0,0</span>}</td>
                  <td className="num"><span className={'cg-recupchip big gm-mono' + (b.current <= 0 ? ' zero' : '')}>{cgNum(b.current)}</span></td>
                  <td style={{ width: 170 }}>
                    <div className="cg-gauge">
                      <span className="cg-gauge-recup" style={{ width: `${Math.min(100, (b.current / 6) * 100)}%` }} />
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="cg-rowact">
                      <button className="cg-iconbtn" title="Créditer une journée" onClick={(ev) => { ev.stopPropagation(); onCredit(r.employee.id); }}>+</button>
                      <button className="cg-iconbtn" title="Poser une récup" disabled={b.current <= 0}
                        onClick={(ev) => { ev.stopPropagation(); onNew(r.employee.id); }}>↧</button>
                    </span>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={7}><div className="cg-empty" style={{ margin: 14 }}>Aucun employé pour ce filtre.</div></td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="sb-foot">
              <td className="gm-foot-lbl">{sorted.length} employés</td>
              <td className="num gm-mono">{cgNum(T.report)}</td>
              <td className="num gm-mono up">+{cgNum(T.cred)}</td>
              <td className="num gm-mono down">−{cgNum(T.pris)}</td>
              <td className="num gm-mono strong">{cgNum(T.stock)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
